import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { tallyAnalytics, type TallyAnalytics } from './api'
import { CountedStatuses, SEAT_ORDER, seatLabel } from './vocab'

/**
 * Is the COUNT healthy? — as distinct from who is winning.
 *
 * The boards answer the second question and had no opinion on the first. Whether returns are
 * still arriving, where verification has stalled, which stations reject ballots at a rate
 * their neighbours do not, and where figures keep being corrected are all invisible in a
 * total — and all of them are what somebody in a tallying hall is actually trying to find
 * out at two in the morning.
 *
 * Every figure states the statuses it counted, the same rule the boards follow.
 */
export function TallyAnalyticsPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const seat = params.get('seat') ?? ''
  const [data, setData] = useState<TallyAnalytics | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!electionId) { setData(null); return }
    try {
      setError(null)
      setData(null)
      setData(await tallyAnalytics({ electionId, seatType: seat || null }))
    } catch (err) {
      setError(explain(err))
    }
  }, [electionId, seat])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="Tally analytics" />

  const peak = Math.max(1, ...(data?.reporting.map((r) => r.unitsReporting) ?? [1]))
  const bandPeak = Math.max(1, ...(data?.turnoutDistribution.map((b) => b.units) ?? [1]))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Analytics</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Not who is winning — whether the count itself is healthy.
          </p>
        </div>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Race</span>
          <select value={seat} className="erp-input h-9 text-erp-sm"
            onChange={(e) => setParams((p) => {
              if (e.target.value) p.set('seat', e.target.value); else p.delete('seat')
              return p
            }, { replace: true })}>
            <option value="">Every race</option>
            {SEAT_ORDER.map((s) => <option key={s} value={s}>{seatLabel(s)}</option>)}
          </select>
        </label>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {data === null && !error && <LoadingBlock />}

      {data && (
        <>
          <CountedStatuses statuses={data.countedStatuses} />

          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Verified returns" value={fmtNumber(data.verification.verified)} />
            <MetricCard label="Usual wait to verify"
              value={duration(data.verification.medianSeconds)}
              sub="half are done faster than this" />
            <MetricCard label="The slowest one in twenty"
              value={duration(data.verification.p95Seconds)}
              sub="waits at least this long" />
            <MetricCard label="Forms read" value={fmtNumber(data.ocr.formsRead)} />
          </div>

          <Note tone="info">
            Latency is entry → verification. Unverified returns are invisible on every board,
            so a queue here looks exactly like "nothing is arriving" — which sends help to
            the wrong place.
          </Note>

          {/* ── reporting over time ────────────────────────────────────── */}
          <div>
            <h3 className="text-erp-sm font-medium mb-1.5">Units reporting, by hour</h3>
            {data.reporting.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">Nothing filed yet.</p>
            ) : (
              <div className="axis-table-scroll">
                <div className="flex items-end gap-1 h-24 min-w-full">
                  {data.reporting.map((r) => (
                    <div key={r.hour} className="flex-1 min-w-[6px]"
                      title={`${fmtDateTime(r.hour)} — ${r.unitsReporting} unit(s)`}>
                      <div className="bg-chocolate rounded-t"
                        style={{ height: `${Math.max(2, (r.unitsReporting / peak) * 96)}px` }} />
                    </div>
                  ))}
                </div>
                <p className="text-erp-xs text-muted-foreground mt-1">
                  A flat stretch means something upstream has stopped — a scanner, a network,
                  a county — which no total will show you.
                </p>
              </div>
            )}
          </div>

          {/* ── turnout spread ────────────────────────────────────────── */}
          <div>
            <h3 className="text-erp-sm font-medium mb-1.5">Turnout spread across units</h3>
            <div className="flex items-end gap-1 h-20">
              {data.turnoutDistribution.map((b) => (
                <div key={b.fromPct} className="flex-1 text-center"
                  title={`${b.fromPct}–${b.toPct}% — ${b.units} unit(s)`}>
                  <div className={cn('rounded-t',
                    b.fromPct >= 90 ? 'bg-amber-500' : 'bg-chocolate')}
                    style={{ height: `${Math.max(2, (b.units / bandPeak) * 64)}px` }} />
                  <span className="block text-erp-xs text-muted-foreground mt-0.5">
                    {b.fromPct}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-erp-xs text-muted-foreground mt-1">
              A healthy count is one hump. Two means two populations; a bar at 90–100% is a
              list of places to visit, not a distribution feature.
            </p>
          </div>

          {/* ── rejected outliers ─────────────────────────────────────── */}
          <div>
            <h3 className="text-erp-sm font-medium mb-1.5">Highest rejected-ballot rates</h3>
            <ErpTable head={(
              <>
                <Th>Place</Th>
                <Th className="text-right">Rejected</Th>
                <Th className="text-right">Cast</Th>
                <Th className="text-right">Rate</Th>
              </>
            )}>
              {data.rejectedOutliers.length === 0 ? (
                <tr><Td className="text-muted-foreground">Nothing counted yet.</Td></tr>
              ) : data.rejectedOutliers.map((o) => (
                <tr key={o.unitId ?? o.unitPath}>
                  <Td>{o.unitName}</Td>
                  <Td className="text-right tabular-nums">{fmtNumber(o.rejectedVotes)}</Td>
                  <Td className="text-right tabular-nums">{fmtNumber(o.totalCast)}</Td>
                  <Td className={cn('text-right tabular-nums font-medium',
                    o.rejectedPct != null && o.rejectedPct > 5
                      && 'text-amber-700 dark:text-amber-400')}>
                    {o.rejectedPct == null ? '—' : `${o.rejectedPct}%`}
                  </Td>
                </tr>
              ))}
            </ErpTable>
            <p className="text-erp-xs text-muted-foreground mt-1">
              A station rejecting 8% where its neighbours reject 1% is a training problem, a
              bad batch of papers, or a dispute — and invisible in any total.
            </p>
          </div>

          {/* ── corrections ───────────────────────────────────────────── */}
          {data.corrections.length > 0 && (
            <div>
              <h3 className="text-erp-sm font-medium mb-1.5">Most-corrected places</h3>
              <ErpTable head={(<><Th>Place</Th><Th className="text-right">Versions</Th></>)}>
                {data.corrections.map((c) => (
                  <tr key={c.unitId ?? c.unitPath}>
                    <Td>{c.unitName}</Td>
                    <Td className="text-right tabular-nums">{c.versions}</Td>
                  </tr>
                ))}
              </ErpTable>
              <p className="text-erp-xs text-muted-foreground mt-1">
                A figure corrected repeatedly at one station is worth a look at the paper.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** Seconds → the coarsest unit that is still honest. "4h" beats "14,400s". */
function duration(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 90) return `${Math.round(seconds)}s`
  if (seconds < 5400) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}
